import { useState } from "react";
import { RootState } from "../../reducers";
import { useSelector, useDispatch } from "react-redux";
import {
  SelectorTitleWithButton,
  SelectorTitleButton,
  SelectorInputOption,
  Fixture,
  FixtureDate,
  TeamsBadge,
  NoFixture,
  StickyButtonContainer,
  StickyButton
} from "../../styles/SelectorStyles";
import dayjs from "dayjs";
import { Event } from "../../types/fixture";
import { sendAnalyticsEvent } from "../../utils/sendAnalyticMessages";

const { isEqual, groupBy, uniqBy } = require('lodash');

interface SelectedGameIdsProps {
  selectedGameIds: string[];
}

interface DayFixtures {
  day: string;
  events: Event[];
}

function GamesSelector({ selectedGameIds }: SelectedGameIdsProps): JSX.Element {
  const parentURL = document.referrer
    ? document.referrer
    : window.location.ancestorOrigins?.[0];

  const [selectedGames, setSelectedGames] = useState(selectedGameIds);

  const dispatch = useDispatch();
  const filterStore = useSelector((state: RootState) => state.filter);

  const fixturesGroupedByDate = groupBy(filterStore.fixtures, 'day');
  const fixturesArr = Object.keys(fixturesGroupedByDate).map(e => {
    return {
      day: e,
      events: fixturesGroupedByDate[e]
    }
  });

  const checkDayFixturesExist = (day: DayFixtures) => {
    return day.events.map((i: Event) => i.id).every((e: string) => selectedGames.includes(e));
  }

  const checkFixtureExist = (fixture: Event) => {
    return selectedGames.includes(fixture.id);
  }

  const buildGameSelections = () => {
    if (selectedGames.length > 0) {
      return selectedGames.map((i: string) => {
        const found = filterStore.fixtures.find((e: Event) => e.id === i);
        return found ? {
          sport: found.type,
          competition: found.meta.competition.id,
          game: `${found.contestant[1].name} @ ${found.contestant[0].name}`,
          date: dayjs(found.date).format('dddd DD/MM/YYYY')
        } : null
      });
    }
    return 'All Games'
  }

  const renderFixtrues = (fixture: Event) => {
    return (
      <Fixture>
        <TeamsBadge>
          <img
            src={`https://cdn.checkd-dev.com/bettinghub/teams/${fixture.contestant[1].id}.png`}
            alt={fixture.contestant[1].name}
            loading="lazy"
          />
          <img
            src={`https://cdn.checkd-dev.com/bettinghub/teams/${fixture.contestant[0].id}.png`}
            alt={fixture.contestant[0].name}
            loading="lazy"
          />
        </TeamsBadge>
        <div>
          <div><b>{fixture.contestant[1].code}</b> @ <b>{fixture.contestant[0].code}</b></div>
          <FixtureDate>{dayjs.utc(fixture.date).local().format("hh:mmA")}</FixtureDate>
        </div>
      </Fixture >

    )
  }

  return fixturesArr.length > 0 ? (
    <div>
      <SelectorTitleWithButton>
        <div>Select Games</div>
        <SelectorTitleButton onClick={() => {
          dispatch({
            type: "SET_GAMES",
            payload: [],
          });
          dispatch({
            type: "SET_SELECTED_FILTER",
            payload: '',
          });
          sendAnalyticsEvent(
            {
              event: "smart_picks_games_select",
              selection: buildGameSelections(),
            },
            parentURL
          );
        }}>Unselect All</SelectorTitleButton>
      </SelectorTitleWithButton>
      {
        fixturesArr.map((d) => (
          <div key={d.day}>
            <SelectorInputOption onClick={() => {
              setSelectedGames(checkDayFixturesExist(d) ?
                selectedGames.filter(e => !d.events.map((i: Event) => i.id).includes(e))
                :
                uniqBy([...selectedGames, ...d.events.map((i: Event) => i.id)]))
            }}>
              <div><b>{d.day}</b></div>
              <div>
                <input
                  type="checkbox"
                  id={d.day}
                  name={d.day}
                  value={d.day}
                  checked={checkDayFixturesExist(d)}
                  readOnly
                />
              </div>
            </SelectorInputOption>
            {
              d.events.map((f: Event) => (
                <SelectorInputOption key={f.id} onClick={() => {
                  setSelectedGames(checkFixtureExist(f) ?
                    selectedGames.filter((i) => i !== f.id)
                    :
                    [...selectedGames, ...[f.id]])
                }}>
                  <div>
                    {renderFixtrues(f)}
                  </div>
                  <div>
                    <input
                      type="checkbox"
                      id={f.id}
                      name={f.type}
                      value={f.id}
                      checked={checkFixtureExist(f)}
                      readOnly
                    />
                  </div>
                </SelectorInputOption>
              ))
            }
          </div>
        ))
      }
      {!isEqual(filterStore.games, selectedGames) &&
        <StickyButtonContainer>
          <StickyButton
            onClick={() => {
              dispatch({
                type: "SET_GAMES",
                payload: selectedGames,
              });
              dispatch({
                type: "SET_SELECTED_FILTER",
                payload: '',
              });
              sendAnalyticsEvent(
                {
                  event: "smart_picks_games_select",
                  selection: buildGameSelections(),
                },
                parentURL
              );
            }}
            role="button"
          >
            Update Results
          </StickyButton>
        </StickyButtonContainer>}
    </div>
  ) : (
    <NoFixture>No Events Available</NoFixture>
  );
}

export default GamesSelector;
